import { useMemo } from "react";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";
import { useAxios } from "@rbxos-vs/default/lib/axios-provider";

/**
 * Transform the configuration object to a URL with query parameters.
 * @param {object} config The configuration object for the API request including filters and sort.
 * @returns {URL} The URL object with the query string.
 */
export const transformConfigToUrl = (config) => {
  const query = new URLSearchParams("");

  const { presetFilters, fields, facets, filters, limit, sort, endpoint } = config;

  /**
   * Rewrite preset filters to url filters
   * @example field_key=filter_value&operator[field_key]=gte
   */
  if (presetFilters) {
    presetFilters.forEach((presetFilter) => {
      query.set(presetFilter.field, presetFilter.value);
      query.set(`operator[${presetFilter.field}]`, presetFilter.operator);
    });
  }

  /**
   * Rewrite field filters to url filters
   * @example field_key=filter_value
   */
  if (filters && fields) {
    fields.forEach((field) => {
      if (filters[field.id]?.length > 0) {
        query.set(field.id, filters[field.id].join(","));
      }
    });
  }

  /**
   * Rewrite facet filters to url filters
   * @example f[0]=filter_key:filter_value
   */
  if (filters && facets) {
    let facetCount = 0;

    facets.forEach((facet) => {
      const facetId = facet.id;
      if (filters[facetId]?.length > 0) {
        filters[facetId].sort();
        filters[facetId].forEach((filterValue) => {
          const key = `f[${facetCount++}]`;
          const value = `${facetId}:${filterValue}`;
          query.set(key, value);
        });
      }
    });
  }

  /**
   * Rewrite sort to url filters
   * @example sort=sortkey&order=asc
   */
  if (sort && !config.query) {
    const sortParams = sort.split(":");
    query.set("sort", sortParams[0]);
    query.set("order", sortParams[1]);
  }

  /**
   * Rewrite limit to url filters
   * @example limit=10
   */
  if (limit) {
    query.set("limit", config.limit);
  }

  /**
   * If querystring is provided, sort by relevance.
   */
  if (config.query) {
    query.set("sort", "search_api_relevance");
    query.set("order", "desc");
  }

  /**
   * Set the current page
   * @example page=0
   */
  query.set("page", config.page);

  /**
   * Set the search query
   * @example q=search_term
   */
  query.set("q", config.query || "");

  /**
   * Cache busting
   * Used, to clear stale response-cache from clients.
   */
  query.set("cache-bust", "v1");

  /**
   * Finally, build the URL and return it
   */
  const queryString = query.toString();
  return `${endpoint}?${queryString}`;
};

/**
 * React hook to simplify the handling of Axios requests.
 * @param {import("axios").AxiosRequestConfig} request Axios request config
 * @returns {{
 *  data: object,
 *  loading: boolean,
 *  error: any,
 *  fetchMore: function
 * }} The first element is an object with the response data,
 */
const useSearchAPIRequest = (config, manualNodes = null) => {
  const axiosClient = useAxios();
  const currentLanguage = useSelector((state) => state.i18n.currentLanguage);

  const queryKey = [
    "search-api",
    currentLanguage,
    config.endpoint,
    config.query,
    config.sort,
    config.limit,
    config.pageSize,
    config.presetFilters,
    config.filters,
    config.fields?.map((f) => f.id) || null,
    config.facets,
  ];

  // Handle manual nodes, in case they are provided.
  // Therefore we skip the query.
  const queryEnabled = !manualNodes?.length;
  const manualData = useMemo(() => {
    if (!manualNodes?.length) return null;

    const base = {
      search_results: manualNodes,
      search_results_count: manualNodes.length,
      search_results_per_page: manualNodes.length,
      search_results_page: 0,
      search_results_pages: 1,
    };
    const q = (config.query || "").trim();

    if (!q.length) return base;

    const lowered = q.toLowerCase();
    const filtered = base.search_results.filter((result) =>
      Object.values(result).some((value) =>
        JSON.stringify(value).toLowerCase().includes(lowered),
      ),
    );

    return {
      ...base,
      search_results: filtered,
      search_results_count: filtered.length,
      search_results_per_page: filtered.length,
    };
  }, [manualNodes, config.query]);

  const {
    data: infiniteData,
    error,
    isPending,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    enabled: queryEnabled,
    initialPageParam: typeof config.page === "number" ? config.page : 0,
    queryFn: async ({ pageParam }) => {
      const _config = { ...config, page: pageParam };
      const url = transformConfigToUrl(_config);
      const response = await axiosClient.get(url);
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      const page = Number(lastPage?.search_results_page ?? 0);
      const pages = Number(lastPage?.search_results_pages ?? 1);
      if (page < pages - 1) return page + 1;
      return undefined;
    },
    staleTime: 30000,
    placeholderData: keepPreviousData,
  });

  const mergedData = useMemo(() => {
    if (manualData) return manualData;
    if (!infiniteData?.pages?.length) return null;

    const pages = infiniteData.pages;
    const last = pages[pages.length - 1];

    const mergedResults = pages.flatMap((p) => p?.search_results ?? []);

    return {
      ...last,
      search_results: mergedResults,
    };
  }, [infiniteData, manualData]);

  const loading = isPending || (isFetching && !isFetchingNextPage);

  // Set the status of the request.
  let status = "IDLE";
  if (manualNodes?.length) {
    status = "IDLE";
  } else if (isFetchingNextPage) {
    status = "LOADING_MORE";
  } else if (loading) {
    status = "LOADING";
  }

  const fetchMore = async () => {
    if (manualNodes?.length) return;
    if (!hasNextPage) return;
    return fetchNextPage();
  };

  return {
    data: mergedData,
    loading,
    status,
    error,
    fetchMore,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  };
};


export default useSearchAPIRequest;